import { FC, useId, useState } from 'react';
import styles from './deep-dive-toggle.module.scss';
import InfoIcon from '@/assets/icons/circle-info.svg?react';
import { classnames } from '@/utils/functions';

interface DeepDiveToggleProps {
  isOn: boolean;
  // Announced as unavailable while the app is about to change it; clicks
  // still reach onToggle and onInfoClick, which are expected to ignore them
  isLocked?: boolean;
  onToggle: (isOn: boolean) => void;
  onInfoClick: () => void;
  className?: string;
}

const DeepDiveToggle: FC<DeepDiveToggleProps> = ({
  isOn,
  isLocked = false,
  onToggle,
  onInfoClick,
  className = '',
}) => {
  const switchId = useId();
  // Animate state changes after mount (toggle, modal pick, thread reset),
  // but not the initial render.
  const [initialIsOn] = useState(isOn);
  const [hasChanged, setHasChanged] = useState(false);
  if (!hasChanged && isOn !== initialIsOn) {
    setHasChanged(true);
  }

  return (
    <div
      className={classnames(styles['deep-dive'], className)}
      data-on={isOn}
      data-animate={hasChanged}
    >
      {/* A native label, so clicking it toggles the switch. */}
      <label
        htmlFor={switchId}
        className={styles['deep-dive-label']}
        data-testid="deep-dive-label"
      >
        <span>
          Deep<span className={styles['deep-dive-label-suffix']}> Dive</span>
        </span>
      </label>
      <button
        type="button"
        aria-label="About Deep Dive"
        title="About Deep Dive"
        data-testid="deep-dive-info"
        aria-disabled={isLocked || undefined}
        className={styles['deep-dive-info']}
        onClick={onInfoClick}
      >
        <InfoIcon className={styles['deep-dive-info-icon']} aria-hidden />
      </button>
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-disabled={isLocked || undefined}
        aria-label="Deep Dive"
        data-testid="deep-dive-toggle"
        className={styles['deep-dive-switch']}
        onClick={() => onToggle(!isOn)}
      >
        <span className={styles['deep-dive-track']} aria-hidden>
          <span className={styles['deep-dive-thumb']} />
        </span>
      </button>
    </div>
  );
};

export default DeepDiveToggle;
